import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import adminAxios from '../adminAxios';
import { formatAgeLabel, formatDuration, formatLockDateTime } from './utils';
import type {
  ManifestBulkFailResponse,
  ManifestFailResponse,
  ManifestInspectorManifest,
  ManifestInspectorResponse,
  ManifestInspectorSummary,
} from './types';

const { Option } = Select;
const { Text } = Typography;

const MANIFEST_KIND_OPTIONS = [
  { value: 'translation', label: 'Translation manifests' },
  { value: 'proofreading', label: 'Proofreading manifests' },
  { value: 'term_scan', label: 'Term scan manifests' },
  { value: 'character_analysis', label: 'Character analysis manifests' },
] as const;

const STATUS_OPTIONS = ['QUEUED', 'LAUNCHING', 'RUNNING', 'FAILED', 'ERROR', 'COMPLETED'] as const;

const statusColorMap: Record<string, string> = {
  QUEUED: 'default',
  LAUNCHING: 'blue',
  RUNNING: 'processing',
  FAILED: 'red',
  ERROR: 'magenta',
  COMPLETED: 'green',
};

const toSentence = (value?: string) => {
  if (!value) return '—';
  const normalized = value.replace(/[_-]/g, ' ').trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const humanizeCount = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  return value.toLocaleString();
};

interface ManifestInspectorProps {
  apiBase: string;
}

const ManifestInspector: React.FC<ManifestInspectorProps> = ({ apiBase }) => {
  const [manifestKind, setManifestKind] = useState<'translation' | 'proofreading' | 'term_scan' | 'character_analysis'>('translation');
  const [userId, setUserId] = useState('');
  const [bookId, setBookId] = useState('');
  const [chapterGuid, setChapterGuid] = useState('');
  const [runId, setRunId] = useState('');
  const [manifestId, setManifestId] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>(['QUEUED', 'LAUNCHING', 'RUNNING']);
  const [onlyStuck, setOnlyStuck] = useState(true);
  const [allowGlobal, setAllowGlobal] = useState(false);
  const [minAgeMinutes, setMinAgeMinutes] = useState(5);
  const [limit, setLimit] = useState(50);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('oldest');
  const [manifests, setManifests] = useState<ManifestInspectorManifest[]>([]);
  const [summary, setSummary] = useState<ManifestInspectorSummary | null>(null);
  const [responseMeta, setResponseMeta] = useState<{
    inspectedAt: string;
    totalMatched: number;
    truncated: boolean;
  } | null>(null);
  const [lastInspectScopeParams, setLastInspectScopeParams] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [failModalManifest, setFailModalManifest] = useState<ManifestInspectorManifest | null>(null);
  const [failReason, setFailReason] = useState('Failed via admin manifest inspector');
  const [failLinkedLock, setFailLinkedLock] = useState(true);
  const [releaseCreditHold, setReleaseCreditHold] = useState(true);
  const [failLoading, setFailLoading] = useState(false);
  const [bulkFailModalVisible, setBulkFailModalVisible] = useState(false);
  const [bulkFailReason, setBulkFailReason] = useState('Bulk failed via admin manifest inspector');
  const [bulkFailLinkedLock, setBulkFailLinkedLock] = useState(true);
  const [bulkReleaseCreditHold, setBulkReleaseCreditHold] = useState(true);
  const [bulkMaxManifests, setBulkMaxManifests] = useState(25);
  const [bulkFailLoading, setBulkFailLoading] = useState(false);

  const hasScope = useMemo(
    () =>
      [userId, bookId, chapterGuid, runId, manifestId].some((value) => value.trim().length > 0),
    [bookId, chapterGuid, manifestId, runId, userId]
  );

  const canInspect = hasScope || allowGlobal;
  const canBulkFail = useMemo(
    () => !loading && !!lastInspectScopeParams && manifests.some((manifest) => !manifest.isTerminal),
    [lastInspectScopeParams, loading, manifests]
  );

  const resetForm = useCallback(() => {
    setManifestKind('translation');
    setUserId('');
    setBookId('');
    setChapterGuid('');
    setRunId('');
    setManifestId('');
    setStatusFilters(['QUEUED', 'LAUNCHING', 'RUNNING']);
    setOnlyStuck(true);
    setAllowGlobal(false);
    setMinAgeMinutes(5);
    setLimit(50);
    setSortOrder('oldest');
    setManifests([]);
    setSummary(null);
    setResponseMeta(null);
    setLastInspectScopeParams(null);
  }, []);

  const inspectManifests = useCallback(async () => {
    if (!canInspect) {
      message.warning('Enter a scope filter or enable global scan.');
      return;
    }
    setLoading(true);
    try {
      const scopeParams: Record<string, any> = {
        manifest_kind: manifestKind,
        user_id: userId.trim() || undefined,
        book_id: bookId.trim() || undefined,
        chapter_guid: chapterGuid.trim() || undefined,
        run_id: runId.trim() || undefined,
        manifest_id: manifestId.trim() || undefined,
        status: statusFilters.length ? statusFilters.join(',') : undefined,
        only_stuck: onlyStuck,
        allow_global: allowGlobal,
        min_age_minutes: minAgeMinutes,
        sort: sortOrder,
      };
      const inspectParams: Record<string, any> = {
        ...scopeParams,
        limit,
      };
      Object.keys(inspectParams).forEach((key) => {
        if (inspectParams[key] === undefined) {
          delete inspectParams[key];
        }
      });
      Object.keys(scopeParams).forEach((key) => {
        if (scopeParams[key] === undefined) {
          delete scopeParams[key];
        }
      });
      const response = await adminAxios.get<ManifestInspectorResponse>(
        `${apiBase}/admin/manifest-inspector`,
        { params: inspectParams }
      );
      setManifests(response.data.manifests || []);
      setSummary(response.data.summary || null);
      setLastInspectScopeParams(scopeParams);
      setResponseMeta({
        inspectedAt: response.data.inspectedAt,
        totalMatched: response.data.totalMatched,
        truncated: response.data.truncated,
      });
      if (response.data.truncated) {
        message.warning('Results truncated to the requested limit.');
      } else {
        message.success(`Loaded ${response.data.manifests.length || 0} manifest(s).`);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to inspect manifests';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  }, [
    allowGlobal,
    apiBase,
    bookId,
    canInspect,
    chapterGuid,
    limit,
    manifestId,
    manifestKind,
    minAgeMinutes,
    onlyStuck,
    runId,
    sortOrder,
    statusFilters,
    userId,
  ]);

  const openFailModal = useCallback((manifest: ManifestInspectorManifest) => {
    setFailModalManifest(manifest);
    setFailReason('Failed via admin manifest inspector');
    setFailLinkedLock(true);
    setReleaseCreditHold(true);
  }, []);

  const closeFailModal = useCallback(() => {
    setFailModalManifest(null);
    setFailReason('Failed via admin manifest inspector');
    setFailLinkedLock(true);
    setReleaseCreditHold(true);
  }, []);

  const openBulkFailModal = useCallback(() => {
    if (!canBulkFail) {
      message.warning('Inspect manifests first, then bulk fail from the loaded results.');
      return;
    }
    setBulkFailModalVisible(true);
    setBulkFailReason('Bulk failed via admin manifest inspector');
    setBulkFailLinkedLock(true);
    setBulkReleaseCreditHold(true);
    setBulkMaxManifests(Math.min(Math.max(manifests.length || 1, 1), 200));
  }, [canBulkFail, manifests.length]);

  const closeBulkFailModal = useCallback(() => {
    setBulkFailModalVisible(false);
  }, []);

  const handleFailManifest = useCallback(async () => {
    if (!failModalManifest?.manifestId) {
      return;
    }
    setFailLoading(true);
    try {
      const payload = {
        manifest_id: failModalManifest.manifestId,
        manifest_kind: failModalManifest.manifestKind || manifestKind,
        reason: failReason.trim() || undefined,
        fail_linked_lock: failLinkedLock,
        release_credit_hold: releaseCreditHold,
      };
      const response = await adminAxios.post<ManifestFailResponse>(
        `${apiBase}/admin/manifest-fail`,
        payload
      );
      const actions = response.data.lockActions || {
        holdReleased: false,
        refundIssued: false,
        lockUpdated: false,
      };
      message.success(
        `Manifest failed. Lock updated: ${actions.lockUpdated ? 'yes' : 'no'}, credits released: ${
          actions.holdReleased || actions.refundIssued ? 'yes' : 'no'
        }.`
      );
      closeFailModal();
      await inspectManifests();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to fail manifest';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setFailLoading(false);
    }
  }, [
    apiBase,
    closeFailModal,
    failLinkedLock,
    failModalManifest,
    failReason,
    inspectManifests,
    manifestKind,
    releaseCreditHold,
  ]);

  const handleBulkFail = useCallback(async () => {
    if (!canBulkFail) {
      message.warning('Inspect manifests first, then bulk fail from the loaded results.');
      return;
    }
    if (!lastInspectScopeParams) {
      message.warning('Run inspect first before bulk fail.');
      return;
    }
    setBulkFailLoading(true);
    try {
      const payload = {
        ...lastInspectScopeParams,
        max_manifests: Math.min(Math.max(bulkMaxManifests, 1), 500),
        reason: bulkFailReason.trim() || undefined,
        fail_linked_lock: bulkFailLinkedLock,
        release_credit_hold: bulkReleaseCreditHold,
        dry_run: false,
      };
      const response = await adminAxios.post<ManifestBulkFailResponse>(
        `${apiBase}/admin/manifest-fail-bulk`,
        payload
      );
      message.success(`Failed ${response.data.manifestsFailedCount || 0} manifest(s).`);
      if ((response.data.failures || []).length > 0) {
        message.warning(`${response.data.failures.length} manifest(s) could not be failed.`);
      }
      closeBulkFailModal();
      await inspectManifests();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to bulk fail manifests';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setBulkFailLoading(false);
    }
  }, [
    apiBase,
    bulkFailLinkedLock,
    bulkFailReason,
    bulkMaxManifests,
    bulkReleaseCreditHold,
    canBulkFail,
    closeBulkFailModal,
    inspectManifests,
    lastInspectScopeParams,
  ]);

  const columns = useMemo<ColumnsType<ManifestInspectorManifest>>(
    () => [
      {
        title: 'Manifest',
        key: 'manifest',
        width: 240,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text code style={{ fontSize: 13 }}>
              {record.manifestId || record.documentId || '—'}
            </Text>
            <Tag color="geekblue" style={{ width: 'fit-content' }}>
              {record.manifestKind === 'term_scan'
                ? 'Term Scan'
                : record.manifestKind === 'character_analysis'
                  ? 'Character Analysis'
                  : record.manifestKind === 'proofreading'
                    ? 'Proofreading'
                  : 'Translation'}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Run: {record.runId || '—'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 220,
        render: (_, record) => {
          const status = record.statusNormalized || record.status || 'UNKNOWN';
          const color = statusColorMap[status] || 'default';
          return (
            <Space direction="vertical" size={0}>
              <Tag color={color} style={{ width: 'fit-content' }}>
                {toSentence(status)}
              </Tag>
              {record.isStuck ? <Tag color="orange">Stuck</Tag> : null}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Activity {formatDuration(record.activityAgeSeconds)}
              </Text>
            </Space>
          );
        },
      },
      {
        title: 'Timing',
        key: 'timing',
        width: 240,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text>
              Activity:{' '}
              <Text type="secondary">{formatLockDateTime(record.activityAt || record.updatedAt)}</Text>
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Age: {formatAgeLabel(record.activityAgeSeconds)}
            </Text>
            {record.queuedAt ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Queued: {formatLockDateTime(record.queuedAt)}
              </Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: 'Scope',
        key: 'scope',
        width: 280,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text type="secondary">User: {record.userId || '—'}</Text>
            <Text type="secondary">Book: {record.bookId || '—'}</Text>
            <Text type="secondary">Chapter: {record.chapterGuid || '—'}</Text>
            <Text type="secondary">
              Lang: {record.sourceLang || '—'} → {record.targetLang || '—'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Diagnostics',
        key: 'diagnostics',
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text type={record.hasError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {record.statusMessage || record.lastLaunchError || record.termScanError || record.analysisError || '—'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Error Kind: {record.lastErrorKind || '—'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Lock: {record.lockKey || '—'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        render: (_, record) => {
          if (record.isTerminal) {
            return <Text type="secondary">—</Text>;
          }
          return (
            <Tooltip title="Marks manifest FAILED and optionally fails linked lock.">
              <Button danger size="small" onClick={() => openFailModal(record)}>
                Fail manifest
              </Button>
            </Tooltip>
          );
        },
      },
    ],
    [openFailModal]
  );

  return (
    <Card title="Manifest Inspector" style={{ marginTop: 16 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8} lg={6}>
              <Select
                value={manifestKind}
                onChange={(value) => setManifestKind(value)}
                style={{ width: '100%' }}
              >
                {MANIFEST_KIND_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="Manifest ID"
                value={manifestId}
                allowClear
                onChange={(event) => setManifestId(event.target.value)}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="Run ID"
                value={runId}
                allowClear
                onChange={(event) => setRunId(event.target.value)}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="User ID"
                value={userId}
                allowClear
                onChange={(event) => setUserId(event.target.value)}
              />
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="Book ID"
                value={bookId}
                allowClear
                onChange={(event) => setBookId(event.target.value)}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="Chapter GUID"
                value={chapterGuid}
                allowClear
                onChange={(event) => setChapterGuid(event.target.value)}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Statuses"
                value={statusFilters}
                onChange={(values) => setStatusFilters(values)}
                style={{ width: '100%' }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <Option key={status} value={status}>
                    {toSentence(status)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={4} lg={3}>
              <InputNumber
                min={0}
                max={10080}
                value={minAgeMinutes}
                onChange={(value) => setMinAgeMinutes(Number(value) || 0)}
                style={{ width: '100%' }}
                addonBefore="Age (m)"
              />
            </Col>
            <Col xs={12} md={4} lg={3}>
              <InputNumber
                min={5}
                max={500}
                value={limit}
                onChange={(value) => setLimit(Number(value) || 50)}
                style={{ width: '100%' }}
                addonBefore="Limit"
              />
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} md={8} lg={5}>
              <Space>
                <Switch checked={onlyStuck} onChange={setOnlyStuck} />
                <span>Only stuck</span>
              </Space>
            </Col>
            <Col xs={24} md={8} lg={5}>
              <Space>
                <Switch checked={allowGlobal} onChange={setAllowGlobal} />
                <span>Allow global scan</span>
              </Space>
            </Col>
            <Col xs={24} md={8} lg={4}>
              <Select value={sortOrder} onChange={(value) => setSortOrder(value)} style={{ width: '100%' }}>
                <Option value="oldest">Oldest first</Option>
                <Option value="newest">Newest first</Option>
              </Select>
            </Col>
            <Col xs={24} md={24} lg={10} style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={resetForm} disabled={loading}>
                  Reset
                </Button>
                <Button type="primary" onClick={inspectManifests} loading={loading} disabled={!canInspect}>
                  Inspect manifests
                </Button>
                <Button danger onClick={openBulkFailModal} disabled={!canBulkFail}>
                  Fail matched (bulk)
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {responseMeta ? (
          <Text type="secondary">
            Last inspected {formatLockDateTime(responseMeta.inspectedAt)} · Matches{' '}
            {humanizeCount(responseMeta.totalMatched)}
            {responseMeta.truncated ? ' (truncated to limit)' : ''}
          </Text>
        ) : null}

        {summary ? (
          <Card type="inner" size="small" title="Summary">
            <Row gutter={16}>
              <Col xs={12} md={6}>
                <Text strong>Total</Text>
                <div>{humanizeCount(summary.count)}</div>
              </Col>
              <Col xs={12} md={6}>
                <Text strong>Active</Text>
                <div>{humanizeCount(summary.activeCount)}</div>
              </Col>
              <Col xs={12} md={6}>
                <Text strong>Stuck</Text>
                <div>{humanizeCount(summary.stuckCount)}</div>
              </Col>
              <Col xs={12} md={6}>
                <Text strong>Errors</Text>
                <div>{humanizeCount(summary.errorCount)}</div>
              </Col>
            </Row>
          </Card>
        ) : null}

        {canInspect && !loading && manifests.length === 0 && responseMeta ? (
          <Alert
            type="info"
            showIcon
            message="No manifests matched the selected filters."
          />
        ) : null}

        <Table<ManifestInspectorManifest>
          dataSource={manifests}
          columns={columns}
          rowKey={(record) => record.documentId || record.manifestId}
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={false}
          size="small"
        />

        <Modal
          title="Fail Manifest"
          visible={!!failModalManifest}
          onCancel={closeFailModal}
          onOk={handleFailManifest}
          confirmLoading={failLoading}
          okButtonProps={{ danger: true }}
          okText="Fail manifest"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This action marks the manifest as FAILED immediately."
            />
            <div>
              <Text strong>Manifest:</Text> <Text code>{failModalManifest?.manifestId || '—'}</Text>
            </div>
            <Input.TextArea
              rows={4}
              value={failReason}
              onChange={(event) => setFailReason(event.target.value)}
              placeholder="Reason shown in diagnostics"
            />
            <Space>
              <Switch checked={failLinkedLock} onChange={setFailLinkedLock} />
              <span>Fail linked lock when present</span>
            </Space>
            <Space>
              <Switch checked={releaseCreditHold} onChange={setReleaseCreditHold} />
              <span>Release/refund lock credits when possible</span>
            </Space>
          </Space>
        </Modal>
        <Modal
          title="Bulk Fail Manifests"
          visible={bulkFailModalVisible}
          onCancel={closeBulkFailModal}
          onOk={handleBulkFail}
          confirmLoading={bulkFailLoading}
          okButtonProps={{ danger: true }}
          okText="Fail matched manifests"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This will fail multiple manifests based on current filters."
            />
            <Text type="secondary">
              Loaded manifests: {humanizeCount(manifests.length)}. Bulk fail uses your current filters and age/stuck settings.
            </Text>
            <InputNumber
              min={1}
              max={500}
              value={bulkMaxManifests}
              onChange={(value) => setBulkMaxManifests(Number(value) || 1)}
              style={{ width: '100%' }}
              addonBefore="Max manifests"
            />
            <Input.TextArea
              rows={4}
              value={bulkFailReason}
              onChange={(event) => setBulkFailReason(event.target.value)}
              placeholder="Reason shown in diagnostics"
            />
            <Space>
              <Switch checked={bulkFailLinkedLock} onChange={setBulkFailLinkedLock} />
              <span>Fail linked lock when present</span>
            </Space>
            <Space>
              <Switch checked={bulkReleaseCreditHold} onChange={setBulkReleaseCreditHold} />
              <span>Release/refund lock credits when possible</span>
            </Space>
          </Space>
        </Modal>
      </Space>
    </Card>
  );
};

export default ManifestInspector;
